import { Classes, Icon16, Intent, intentColor, intentIcon, Position, Spinner, Toaster } from '@pinpointhq/thumbtack';
import { isInteger, uniqueId } from 'lodash';
import * as React from 'react';

type FlashMessageType = 'alert' | 'error' | 'notice' | 'success';

/**
 * Takes a type and text
 */
export interface IFlashMessage {
  isLoading?: boolean;
  key?: string;
  type?: FlashMessageType;
  text: React.ReactNode;
  timeout?: number;
}

interface IFlashToaster {
  flashMessages: IFlashMessage[];
}

export function createToast(toast: IFlashMessage) {
  const customEvent = new CustomEvent<IFlashMessage>('toasts:create', { detail: toast });
  document.dispatchEvent(customEvent);
}

// would be nice if the options could take functions too.
export function promiseToast<T = any>(
  promise: Promise<T>,
  {
    loading,
    success,
    error,
    key = uniqueId(),
  }: {
    loading?: string;
    success?: string;
    error?: string | ((error: Error) => string);
    key?: string;
  },
) {
  if (loading) createToast({ text: loading, key, isLoading: true });

  promise
    .then(() => {
      if (success) createToast({ text: success, key, isLoading: false, type: 'success' });
    })
    .catch((e) => {
      if (typeof error === 'function') {
        const text = error(e);
        createToast({ text, key, isLoading: false, type: 'error' });
      } else if (error) createToast({ text: error, key, isLoading: false, type: 'error' });
    });

  return promise;
}

export function dismissToast(key: string) {
  const customEvent = new CustomEvent<string>('toasts:dismiss', { detail: key });
  document.dispatchEvent(customEvent);
}

export function FlashToaster({ flashMessages }: IFlashToaster) {
  const toastIntent: Record<FlashMessageType, Intent> = {
    alert: Intent.WARNING,
    error: Intent.DANGER,
    notice: Intent.PRIMARY,
    success: Intent.SUCCESS,
  };
  const toasterRef = React.useRef<Toaster>();

  const toastEventListener = (e: CustomEvent<IFlashMessage>) => showToast(e.detail);

  React.useEffect(() => {
    document.addEventListener('toasts:create', toastEventListener);
    return () => {
      document.removeEventListener('toasts:create', toastEventListener);
    };
  }, []);

  function showToast({ isLoading, key, type, text, timeout }: IFlashMessage) {
    const intent = toastIntent[type] || Intent.NONE;
    const icon = isLoading ? <Spinner small={true} /> : <Icon16 icon={intentIcon(intent)} fill={intentColor(intent)} />;
    const defaultTimeout = isLoading ? 0 : 8000;
    toasterRef.current.show(
      {
        icon,
        intent,
        message: text,
        timeout: isInteger(timeout) ? timeout : defaultTimeout,
      },
      key,
    );
  }

  const dismissToastEventListener =
    (_event: CustomEvent<string>) =>
    ({ detail: key }) => {
      toasterRef.current.dismiss(key);
    };

  React.useEffect(() => {
    document.addEventListener('toasts:dismiss', dismissToastEventListener);
    return () => {
      document.removeEventListener('toasts:dismiss', dismissToastEventListener);
    };
  }, []);

  React.useEffect(() => {
    flashMessages.forEach((flashMessage) => showToast(flashMessage));
  }, []);

  // If we use a portal we don't have a way of setting the portal container so we run into
  // turbolinks issues
  return <Toaster ref={toasterRef} className={Classes.DARK} position={Position.BOTTOM} usePortal={false} />;
}

export default FlashToaster;
